/**
 * DepositVerificationModal — buyer uploads the escrow receipt for an accepted
 * offer's earnest money.
 *
 * Earnest money goes to a title company or escrow agent after the seller
 * accepts, so this opens from an accepted offer's card, not at submission.
 * The receipt is buyer-uploaded and not independently verified. The amount
 * and deposit date are stored in the record's attested_balance /
 * attested_statement_date fields.
 */
import React, { useEffect, useId, useMemo, useState } from 'react';
import DepositVerificationAgent from '../../agents/depositVerificationAgent';
import Modal from '../ui/Modal';
import './DepositVerificationModal.css';

const ESCROW_RECEIPT = 'escrow_receipt';
const MAX_BYTES = 10 * 1024 * 1024;

const DepositVerificationModal = ({ open, offerId, earnestAmount, onComplete, onDismiss }) => {
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState('');
  const [depositDate, setDepositDate] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Stable ids for label / aria-describedby pairing.
  const introId = useId();
  const fileInputId = useId();
  const fileHelpId = useId();
  const amountId = useId();
  const amountHelpId = useId();
  const dateId = useId();
  const notesId = useId();

  // Reset every time the modal opens for a new offer
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setAmount(earnestAmount ? String(Math.round(earnestAmount)) : '');
    setDepositDate('');
    setNotes('');
    setError(null);
  }, [open, offerId, earnestAmount]);

  const fileError = useMemo(() => {
    if (!file) return null;
    if (file.type !== 'application/pdf') return 'Only PDF files are accepted.';
    if (file.size > MAX_BYTES) return 'File exceeds the 10 MB limit.';
    if (file.size === 0) return 'Selected file is empty.';
    return null;
  }, [file]);

  const canUpload = !!file && !fileError && !submitting;

  const handleUpload = async () => {
    if (!canUpload) return;
    setSubmitting(true);
    setError(null);
    try {
      const record = await DepositVerificationAgent.uploadDocument(offerId, {
        file,
        documentType: ESCROW_RECEIPT,
        attestedBalance: amount === '' ? null : Number(amount),
        attestedStatementDate: depositDate || null,
        buyerNotes: notes || null,
      });
      onComplete?.(record);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const amountDiffers =
    earnestAmount &&
    amount !== '' &&
    Number(amount) > 0 &&
    Number(amount) !== Math.round(Number(earnestAmount));

  // Modal owns Escape / overlay click — route through onDismiss when given.
  const handleClose = () => {
    if (submitting) return;
    if (onDismiss) onDismiss();
  };

  const footer = (
    <div className="dv-footer">
      <div className="dv-footer-right">
        {onDismiss && (
          <button
            type="button"
            className="dv-btn dv-btn--secondary"
            onClick={onDismiss}
            disabled={submitting}
          >
            Do this later
          </button>
        )}
        <button
          type="button"
          className="dv-btn dv-btn--primary"
          onClick={handleUpload}
          disabled={!canUpload}
        >
          {submitting ? 'Uploading…' : 'Upload escrow receipt'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Upload your escrow receipt"
      size="md"
      describedBy={introId}
      footer={footer}
    >
      <p id={introId} className="dv-lede">
        When your title company or escrow agent receives your earnest money
        {earnestAmount ? <> of <strong>${Number(earnestAmount).toLocaleString('en-US')}</strong></> : ''},
        they give you a receipt. Upload it here so the seller can see the deposit is in escrow.
      </p>

      <div className="dv-callout">
        <strong>🔒 Tip:</strong> Black out any full account numbers before uploading.
        The seller only needs to see who holds the deposit, the amount and the date.
      </div>

      <div className="dv-field">
        <label htmlFor={fileInputId} className="dv-label">
          Escrow receipt <span className="dv-muted">(PDF, max 10 MB)</span>
        </label>
        <input
          id={fileInputId}
          type="file"
          accept="application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          disabled={submitting}
          aria-describedby={fileHelpId}
        />
        <small id={fileHelpId} className="dv-muted">
          The receipt or deposit confirmation from your title company or escrow agent.
        </small>
        {file && !fileError && (
          <span className="dv-file-info">
            {file.name} · {(file.size / 1024).toFixed(0)} KB
          </span>
        )}
        <div role="status" aria-live="polite" className="dv-error-region">
          {fileError ? <span className="dv-error">{fileError}</span> : ''}
        </div>
      </div>

      <div className="dv-grid">
        <div className="dv-field">
          <label htmlFor={amountId} className="dv-label">
            Amount deposited <span className="dv-muted">(optional)</span>
          </label>
          <div className="dv-input-prefix">
            <span aria-hidden="true">$</span>
            <input
              id={amountId}
              type="text"
              inputMode="numeric"
              value={amount ? Number(amount).toLocaleString('en-US') : ''}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="e.g. 4,250"
              disabled={submitting}
              aria-describedby={amountHelpId}
            />
          </div>
          {amountDiffers ? (
            <span id={amountHelpId} className="dv-warn">
              This doesn&apos;t match the earnest money in your offer. Check the receipt.
            </span>
          ) : (
            <small id={amountHelpId} className="dv-muted">
              As shown on the receipt.
            </small>
          )}
        </div>

        <div className="dv-field">
          <label htmlFor={dateId} className="dv-label">
            Date deposited <span className="dv-muted">(optional)</span>
          </label>
          <input
            id={dateId}
            type="date"
            value={depositDate}
            onChange={(e) => setDepositDate(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="dv-field">
        <label htmlFor={notesId} className="dv-label">
          Notes for the seller <span className="dv-muted">(optional)</span>
        </label>
        <textarea
          id={notesId}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. the title company's name and your file number."
          disabled={submitting}
          maxLength={500}
        />
      </div>

      {error && <div className="dv-error-banner" role="alert">{error}</div>}
    </Modal>
  );
};

export default DepositVerificationModal;
